import './App.css';
import { Helmet } from 'react-helmet';
import React, { useEffect, useState } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { generatePersonSchema, generateWebsiteSchema, generateOrganizationSchema } from './utils/generateJsonLd';
import Ranking from './components/Ranking';
import Navbar from './components/ui/Navbar';
import LeaderboardPage from './components/Leaderboards/LeaderboardPage';
import { AuthProvider } from './contexts/AuthContext';
import { usePersons } from './hooks/usePersons';
import ErrorBoundary from './components/ErrorBoundary';

const AMAZON_AD_ID = '5780916f-3f83-44af-86ee-c03da2240e13';

function App() {
  const { persons } = usePersons();
  const location = useLocation();

  // Detect if we're in react-snap prerendering environment (Puppeteer/Headless Chrome)
  // This prevents UI from being prerendered, avoiding hydration mismatches
  const isPrerendering = typeof navigator !== 'undefined' &&
    /ReactSnap|HeadlessChrome|Puppeteer/i.test(navigator.userAgent);

  // Only render UI on actual client, not during prerendering
  const [isClient, setIsClient] = useState(!isPrerendering);

  useEffect(() => {
    if (!isPrerendering) {
      setIsClient(true);
    }
  }, [isPrerendering]);

  useEffect(() => {
    const existingScript = document.querySelector(`script[src*="${AMAZON_AD_ID}"]`);
    if (existingScript) return;

    const script = document.createElement('script');
    script.src = `//z-na.amazon-adsystem.com/widgets/onejs?MarketPlace=US&adInstanceId=${AMAZON_AD_ID}`;
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  const getSEOData = () => {
    switch (location.pathname) {
      case '/leaderboards':
        return {
          title: 'Leaderboards | Karma Rankings',
          description: 'View the Hall of Fame, Hall of Shame, and top karma donors on Karma Rankings.'
        };
      default:
        return {
          title: 'Karma Rankings | Vote on Public Figures',
          description: 'Karma Rankings allows people to give or take Karma from public figures. Vote now and see who deserves good karma!'
        };
    }
  };

  const seoData = getSEOData();

  return (
    <AuthProvider>
      {/* SEO meta tags and JSON-LD - always rendered (for prerendering) */}
      <Helmet>
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <link rel="canonical" href={`https://www.karmaranking.com${location.pathname}`} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://www.karmaranking.com${location.pathname}`} />
        <meta property="og:image" content="https://www.karmaranking.com/logo512.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />

        <script type="application/ld+json">
          {JSON.stringify(generateWebsiteSchema())}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(generateOrganizationSchema())}
        </script>
        {persons.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify(generatePersonSchema(persons))}
          </script>
        )}
      </Helmet>

      {/* UI only renders on client to avoid react-snap hydration issues */}
      {isClient && (
        <div className="App">
          <Navbar />
          <div id={`amzn-assoc-ad-${AMAZON_AD_ID}`} className="ad-container"></div>

          <ErrorBoundary>
            <Routes>
              <Route path="/" element={<Ranking persons={persons} />} />
              <Route path="/leaderboards" element={<LeaderboardPage persons={persons} />} />
            </Routes>
          </ErrorBoundary>
        </div>
      )}
    </AuthProvider>
  );
}

export default App;
